import type { DemPoExtractOrder, DemPoExtractRow } from '../types/dem';
import {
  registerExtractAbortController,
  unregisterExtractAbortController,
} from './demExtractAbort';

const EXTRACT_TIMEOUT_MS = 120_000;

function getProxyKey(): string {
  const proxyKey = import.meta.env.VITE_DEM_EMAIL_PROXY_KEY?.trim();
  if (!proxyKey) {
    throw new Error('VITE_DEM_EMAIL_PROXY_KEY is not configured for WhatsApp extraction.');
  }
  return proxyKey;
}

function parseQty(raw: unknown): number {
  const cleaned = String(raw ?? '0').replace(/[^\d.]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

/** Standalone packing / UOM words that are not products (e.g. "one packet" after an item). */
const PACKING_ONLY_RE =
  /^(packets?|packs?|pkts?|ctns?|cartons?|boxes?|bags?|bdls?|bundles?|pcs|pieces?|units?|包|盒|箱|袋|条|打)$/i;

function isPackingOnlyDescription(desc: string): boolean {
  return PACKING_ONLY_RE.test(String(desc || '').trim());
}

/** Map spoken packing words to short UOM (keep product description untouched). */
function packingWordToUom(word: string): string {
  const w = String(word || '').trim().toLowerCase();
  if (/^packets?$|^packs?$|^pkts?$/.test(w)) return 'pkt';
  if (/^ctns?$|^cartons?$/.test(w)) return 'ctn';
  if (/^boxes?$|^箱$/.test(w)) return 'box';
  if (/^bags?$|^袋$/.test(w)) return 'bag';
  if (/^bdls?$|^bundles?$/.test(w)) return 'bdl';
  if (/^pcs$|^pieces?$|^units?$/.test(w)) return 'pcs';
  if (w === '盒') return '盒';
  if (w === '包') return '包';
  if (w === '条') return '条';
  if (w === '打') return '打';
  return String(word || '').trim();
}

/**
 * AI sometimes splits "one large carrier bag" + "one packet" into 2 rows.
 * Merge packing-only row into previous: keep description/qty, set UOM (e.g. pkt).
 */
function mergePackingOnlyRows(rows: DemPoExtractRow[]): DemPoExtractRow[] {
  const out: DemPoExtractRow[] = [];
  for (const row of rows) {
    const desc = String(row.description || '').trim();
    if (
      out.length > 0 &&
      isPackingOnlyDescription(desc) &&
      !String(row.items || '').trim()
    ) {
      const prev = out[out.length - 1];
      if (!String(prev.uom || '').trim()) {
        prev.uom = packingWordToUom(desc);
      }
      continue;
    }
    out.push({ ...row });
  }
  return out;
}

function normalizeWaRows(rowsRaw: unknown): DemPoExtractRow[] {
  if (!Array.isArray(rowsRaw)) return [];
  const rows: DemPoExtractRow[] = [];
  for (const row of rowsRaw) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const desc = String(r.description ?? r.Description ?? r.product ?? '').trim();
    const items = String(r.items ?? r.Items ?? r.item_code ?? r.Code ?? '').trim();
    const qty = parseQty(r.quantity ?? r.Quantity ?? r.qty);
    if (qty <= 0) continue;
    if (!desc && !items) continue;
    rows.push({
      items,
      description: desc,
      quantity: String(qty),
      uom: String(r.uom ?? r.UOM ?? r.unit ?? '').trim(),
      unit_price: String(r.unit_price ?? r.price ?? '0').trim(),
    });
  }
  return mergePackingOnlyRows(rows);
}

function normalizeWaExtractResponse(json: unknown, customerId: string): DemPoExtractOrder {
  const root = json as Record<string, unknown>;
  const chatTitle = customerId.trim();
  const withChatTitle = (name: string) => {
    const n = name.trim();
    if (!chatTitle) return n;
    if (!n) return chatTitle;
    if (n.toLowerCase().includes(chatTitle.toLowerCase())) return n;
    return `${chatTitle} ${n}`.trim();
  };

  const orders = Array.isArray(root.orders) ? root.orders : [];
  if (orders.length > 0) {
    const first = orders[0] as Record<string, unknown>;
    const header = (first.header ?? {}) as Record<string, unknown>;
    return {
      header: {
        company_name: withChatTitle(String(header.company_name ?? '')),
        company_address: String(header.company_address ?? '').trim(),
        po_number: String(header.po_number ?? '').trim(),
        delivery_date: String(header.delivery_date ?? '').trim(),
        source_file: 'WhatsApp',
      },
      rows: normalizeWaRows(first.rows),
    };
  }

  const wa = (root.waExtract ?? root) as Record<string, unknown>;
  const customerString = String(wa.customer_string ?? customerId).trim();
  return {
    header: {
      company_name: withChatTitle(customerString),
      company_address: '',
      po_number: '',
      delivery_date: '',
      source_file: 'WhatsApp',
    },
    rows: normalizeWaRows(wa.rows),
  };
}

/** Strip voice-import banners; keep spoken/transcript body for the existing WA extract API. */
function prepareWaMessageForExtract(message: string): string {
  return message
    .replace(/\[Voice transcript[^\]]*\]\s*/gi, '')
    .replace(/\[🎙️ Voice Note Captured\]\s*/g, '')
    .trim();
}

export async function extractWaOrderFromMessage(
  customerId: string,
  message: string,
): Promise<DemPoExtractOrder> {
  const trimmed = message.trim();
  if (!trimmed) {
    throw new Error('WhatsApp message is empty.');
  }

  const forExtract = prepareWaMessageForExtract(trimmed);
  const looksLikeVoice =
    /\[Voice transcript/i.test(trimmed) || trimmed.includes('[🎙️ Voice Note Captured]');
  if (looksLikeVoice && forExtract.length < 5) {
    throw new Error(
      'Voice transcript is empty or too short. Play the note, click BTH Order again after transcription, or type the order manually.',
    );
  }
  if (!forExtract) {
    throw new Error('WhatsApp message is empty.');
  }

  const controller = new AbortController();
  registerExtractAbortController(controller);
  const timer = window.setTimeout(() => controller.abort(), EXTRACT_TIMEOUT_MS);
  try {
    const res = await fetch('/po-extract-api', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-DEM-Email-Key': getProxyKey(),
      },
      body: JSON.stringify({
        extractType: 'whatsapp',
        waCustomerId: customerId,
        textContent: forExtract,
        fileName: 'WhatsApp',
      }),
      signal: controller.signal,
    });
    const text = await res.text();
    if (!res.ok) {
      let messageText = `Extraction failed (${res.status})`;
      try {
        const err = JSON.parse(text) as { error?: string; detail?: string; hint?: string };
        if (err.error) messageText = err.error;
        if (err.detail?.trim()) messageText = `${messageText}: ${err.detail.trim().slice(0, 220)}`;
        if (err.hint?.trim()) messageText = `${messageText} (${err.hint.trim()})`;
      } catch {
        if (text.trim()) messageText = text.trim().slice(0, 300);
      }
      throw new Error(messageText);
    }

    const json = JSON.parse(text) as unknown;
    const order = normalizeWaExtractResponse(json, customerId);
    if (order.rows.length === 0) {
      throw new Error('No order lines with quantity were found in this WhatsApp message.');
    }
    return order;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new Error('AI extraction was cancelled.');
    }
    throw e;
  } finally {
    window.clearTimeout(timer);
    unregisterExtractAbortController(controller);
  }
}
